/**
 * Document Compliance Dashboard Component
 * TASK-361: Document Management & Compliance System
 *
 * Displays compliance statistics, expiring documents, and compliance
 * breakdown by category for monitoring document health across clients.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  ComplianceSummary,
  ComplianceByCategory,
  ExpiringDocument,
  DocType,
  getDocTypeLabel,
  getComplianceRateTextColor,
  getComplianceRateBarColor,
} from '../../types/documentCompliance';
import { secureUploadLinkService } from '../../services/secureUploadLinkService';
import ExpiringDocumentsList from './ExpiringDocumentsList';

interface DocumentComplianceDashboardProps {
  warningDays?: number;
}

const DocumentComplianceDashboard: React.FC<DocumentComplianceDashboardProps> = ({
  warningDays = 30,
}) => {
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [categories, setCategories] = useState<ComplianceByCategory[]>([]);
  const [expiringDocs, setExpiringDocs] = useState<ExpiringDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View state
  const [selectedTab, setSelectedTab] = useState<'overview' | 'expiring' | 'expired'>('overview');
  const [selectedCategory, setSelectedCategory] = useState<DocType | null>(null);

  // Fetch compliance data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryData, categoriesData, expiringData] = await Promise.all([
        secureUploadLinkService.getComplianceSummary(warningDays),
        secureUploadLinkService.getComplianceByCategory(warningDays),
        secureUploadLinkService.getExpiringDocuments({ days_ahead: warningDays }),
      ]);

      setSummary(summaryData);
      setCategories(categoriesData.categories);
      setExpiringDocs(expiringData.documents);
    } catch (err) {
      console.error('Failed to fetch compliance data:', err);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [warningDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Loading state
  if (loading && !summary) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#002B45] animate-spin mr-2" />
          <span className="text-gray-600">Loading compliance data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center text-red-600">
          <AlertCircle className="w-8 h-8 mb-2" />
          <span>{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#002B45]" />
          <h3 className="text-lg font-medium text-gray-900">Compliance Dashboard</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Compliance Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-[#002B45]" />
            <span className={`text-2xl font-bold ${getComplianceRateTextColor(summary.compliance_rate)}`}>
              {Math.round(summary.compliance_rate)}%
            </span>
          </div>
          <p className="text-sm text-gray-600">Compliance Rate</p>
        </div>

        {/* Up to Date */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-green-600">
              {summary.up_to_date}
            </span>
          </div>
          <p className="text-sm text-gray-600">Up to Date</p>
        </div>

        {/* Expiring Soon */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-2xl font-bold text-amber-600">
              {summary.expiring_soon}
            </span>
          </div>
          <p className="text-sm text-gray-600">Expiring Soon</p>
        </div>

        {/* Expired */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-2xl font-bold text-red-600">
              {summary.expired}
            </span>
          </div>
          <p className="text-sm text-gray-600">Expired</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setSelectedTab('overview')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              selectedTab === 'overview'
                ? 'text-[#002B45] border-b-2 border-[#002B45]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            By Category
          </button>
          <button
            onClick={() => setSelectedTab('expiring')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              selectedTab === 'expiring'
                ? 'text-[#002B45] border-b-2 border-[#002B45]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Expiring Soon
            {summary.expiring_soon > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 rounded">
                {summary.expiring_soon}
              </span>
            )}
          </button>
          <button
            onClick={() => setSelectedTab('expired')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              selectedTab === 'expired'
                ? 'text-[#002B45] border-b-2 border-[#002B45]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Expired
            {summary.expired > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-800 rounded">
                {summary.expired}
              </span>
            )}
          </button>
        </div>

        <div className="p-4">
          {/* Category Overview */}
          {selectedTab === 'overview' && (
            <div className="space-y-3">
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No documents tracked yet</p>
                </div>
              ) : (
                categories.map((category) => (
                  <div
                    key={category.category}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedCategory(
                      selectedCategory === category.category ? null : category.category
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {getDocTypeLabel(category.category)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.total} documents
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Mini Stats */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={12} />
                          {category.up_to_date}
                        </span>
                        <span className="flex items-center gap-1 text-amber-600">
                          <AlertTriangle size={12} />
                          {category.expiring_soon}
                        </span>
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle size={12} />
                          {category.expired}
                        </span>
                      </div>

                      {/* Compliance Rate */}
                      <div className="w-20">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">Rate</span>
                          <span className={`font-medium ${getComplianceRateTextColor(category.compliance_rate)}`}>
                            {Math.round(category.compliance_rate)}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getComplianceRateBarColor(category.compliance_rate)}`}
                            style={{ width: `${category.compliance_rate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Expiring Documents */}
          {selectedTab === 'expiring' && (
            <ExpiringDocumentsList
              documents={expiringDocs.filter((d) => d.days_until_expiry >= 0)}
              emptyMessage="No documents expiring in the next 30 days"
              onRefresh={fetchData}
            />
          )}

          {/* Expired Documents */}
          {selectedTab === 'expired' && (
            <ExpiringDocumentsList
              documents={expiringDocs.filter((d) => d.days_until_expiry < 0)}
              emptyMessage="No expired documents"
              onRefresh={fetchData}
              showAsExpired
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentComplianceDashboard;
